import { QBS_DETAILS_DATA_TEST_ID } from '@e2e/constants/exam-management';
import type { GroupOption } from '@exam-management/common/types';
import { QBankStructureInformationFormFields } from '@exam-management/components';
import {
  QBankStructureStatus,
  QBankStructureType,
  OrderType,
  useSearchQBankStructureGroupTagsLazyQuery,
} from '@exam-management/graphql';
import { useQBankStructure } from '@exam-management/qbs-provider';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, DialogActions, DialogContent, Divider, Form } from '@shared/components';
import { useDidUpdate } from '@shared/hooks';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const NAME_MIN_LENGTH = 1;
const NAME_MAX_LENGTH = 1024;
const DESCRIPTION_MAX_LENGTH = 1024;
const REMARK_MAX_LENGTH = 1024;
const GROUP_NAME_MAX_LENGTH = 256;

const EditQBankStructureFormSchema = z.object({
  id: z.string(),
  name: z
    .string()
    .trim()
    .min(NAME_MIN_LENGTH, {
      message: 'กรุณากรอกชื่อคลังข้อสอบ',
    })
    .max(NAME_MAX_LENGTH, {
      message: `ชื่อคลังข้อสอบต้องมีความยาวไม่เกิน ${NAME_MAX_LENGTH} ตัวอักษร`,
    })
    .optional(),
  description: z
    .string()
    .trim()
    .max(DESCRIPTION_MAX_LENGTH, {
      message: `รายละเอียดคลังข้อสอบต้องมีความยาวไม่เกิน ${DESCRIPTION_MAX_LENGTH} ตัวอักษร`,
    }),
  type: z.nativeEnum(QBankStructureType).optional(),
  remark: z
    .string()
    .trim()
    .max(REMARK_MAX_LENGTH, {
      message: ` หมายเหตุคลังข้อสอบต้องมีความยาวไม่เกิน ${REMARK_MAX_LENGTH} ตัวอักษร`,
    }),
  status: z.nativeEnum(QBankStructureStatus),
  group: z
    .object({
      inputValue: z.string().nullish(),
      label: z.string(),
      value: z.string(),
    })
    .nullish()
    .refine(
      (data) =>
        !data ||
        ((data.inputValue?.length ?? 0) <= GROUP_NAME_MAX_LENGTH &&
          data.value.length <= GROUP_NAME_MAX_LENGTH),
      {
        message: `ชื่อกลุ่มต้องมีความยาวไม่เกิน ${GROUP_NAME_MAX_LENGTH} ตัวอักษร`,
      },
    ),
});

export interface QBankStructureDetailEditFormInput {
  id: string;
  name: string;
  description: string;
  remark: string;
  type: QBankStructureType;
  status: QBankStructureStatus;
  group: GroupOption | null;
}

interface QBankStructureDetailEditFormProps {
  onClose: () => void;
  loading?: boolean;
  onSubmit: (formValues: QBankStructureDetailEditFormInput) => void;
  status: QBankStructureStatus;
}

export default function QBankStructureDetailEditForm(
  props: Readonly<QBankStructureDetailEditFormProps>,
) {
  const { loading: isLoading = false, onClose, onSubmit, status } = props;
  const { qBankStructureInformation } = useQBankStructure();

  const formContext = useForm<QBankStructureDetailEditFormInput>({
    defaultValues: {
      id: qBankStructureInformation?.id,
      name: qBankStructureInformation?.name,
      description: qBankStructureInformation?.description ?? '',
      remark: qBankStructureInformation?.remark ?? '',
      type: qBankStructureInformation?.type,
      status: qBankStructureInformation?.status,
      group: qBankStructureInformation.groupTag
        ? {
            label: qBankStructureInformation.groupTag.name,
            value: qBankStructureInformation.groupTag.name,
          }
        : null,
    },
    resolver: zodResolver(EditQBankStructureFormSchema),
  });

  // NOTE: It's duplicate with create qbank-structure libs, this should be refactor to the same hook
  // But these blocks of codes are still in apps, please refactor this when it's move to libs
  const [groupSearchInput, setGroupSearchInput] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isGroupOpen, setIsGroupOpen] = useState(false);

  const [searchQBankStructureGroupTags, { data: groupData, loading: isGroupLoading }] =
    useSearchQBankStructureGroupTagsLazyQuery();

  const getQBankStructureGroupTags = (currentValue = '') => {
    searchQBankStructureGroupTags({
      fetchPolicy: 'network-only',
      variables: {
        search: {
          name: isSearching ? groupSearchInput : currentValue,
        },
        paging: {
          currentPage: 0,
          pageSize: 100,
        },
        order: {
          field: 'name',
          type: OrderType.Asc,
        },
      },
    });
  };

  const { handleSubmit, reset } = formContext;

  const handleFormSubmit = async (data: QBankStructureDetailEditFormInput) => {
    onSubmit(data);
    reset(data);
  };

  const handleGroupInputChange = (input: string) => {
    setGroupSearchInput(input);
    setIsSearching(true);
  };

  const handleGroupChange = (input: string) => {
    setGroupSearchInput(input);
    setIsSearching(false);
  };

  const handleGroupOpen = (currentValue: string) => {
    getQBankStructureGroupTags(currentValue);
    setIsGroupOpen(true);
  };

  const handleGroupClose = () => {
    setIsGroupOpen(false);
  };

  const handleGroupBlur = (currentValue: string) => {
    setGroupSearchInput(currentValue);
    setIsSearching(false);
  };

  const handleGroupClear = () => {
    setGroupSearchInput('');
    setIsSearching(isGroupOpen); // Fetch data when the group menu is open only
  };

  useDidUpdate(() => {
    const groupDebounceHandler = setTimeout(() => {
      if (isSearching && isGroupOpen) {
        setIsSearching(false);
        getQBankStructureGroupTags();
      }
    }, 700);
    return () => clearTimeout(groupDebounceHandler);
  }, [groupSearchInput]);

  const groupOptions = useMemo(() => {
    return !isGroupLoading && groupData && !isSearching
      ? groupData.qbsSearchQBankStructureGroupTags.tags.map<GroupOption>((tag) => {
          return { label: tag.name, value: tag.name };
        })
      : [];
  }, [isGroupLoading, groupData, isSearching]);

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <QBankStructureInformationFormFields
            status={status}
            dataTestId={QBS_DETAILS_DATA_TEST_ID.EDIT_FORM}
            slotProps={{
              group: {
                options: groupOptions,
                isLoading: isGroupLoading || isSearching,
                onChange: handleGroupChange,
                onInputChange: handleGroupInputChange,
                onOpen: handleGroupOpen,
                onClose: handleGroupClose,
                onBlur: handleGroupBlur,
                onClear: handleGroupClear,
              },
            }}
          />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button variant='outlined' color='secondary' size='large' onClick={onClose}>
            Cancel
          </Button>
          <Button color='primary' size='large' type='submit' loading={isLoading}>
            Save
          </Button>
        </DialogActions>
      </Form>
    </FormProvider>
  );
}
